import { Box, Checkbox, FormControl, FormControlLabel, FormLabel, Grid, Link, Typography } from "@mui/material";
import { FC, ReactElement, useCallback, useMemo } from "react";
import { ContactDto, OrderByType, OrderReviewDto, OrderReviewLineItemDto } from "../../../dtos";
import { BilledToType } from "../../../dtos/enums/BilledToType";
import { LineItemTypes } from "../../../util";
import { DatePicker, EquipmentTypeSelect, OrderBySelect, PricingMethodTypeSelect, TimePicker, UnitOfMeasureSelect } from "../../CommonInputs";
import { IEntityAutocomplete } from "../../CommonInputs/IEntityAutocomplete";
import { FormInput, FormNumberInput } from "../../CoreLib/library";
import { OrderRelatedLinks } from "../OrderForm/OrderRelatedLinks";

export interface ITicketFormGeneralTabProps {
    orderReview: OrderReviewDto;
    orderLineItem: OrderReviewLineItemDto;
}

export const TicketFormGeneralTab: FC<ITicketFormGeneralTabProps> = (props) => {
    const { orderReview, orderLineItem } = props;

    const isFob = useMemo(() => {
        return orderReview?.isFobQuote;
    }, [orderReview]);

    const isMaterialLineItem = useCallback((orderLineItem: OrderReviewLineItemDto) => {
        return orderLineItem?.lineItem?.type === LineItemTypes.Material && !isFob;
    }, [isFob]);

    const currentQuote = useMemo(() => {
        return orderReview?.quote;
    }, [orderReview?.quote]);

    const gridItem = useCallback((content: ReactElement) => {
        return (
            <Grid
                item
                sx={{
                    '--Grid-borderWidth': '1px',
                    border: 'var(--Grid-borderWidth) solid',
                    borderColor: 'divider',
                    borderCollapse: 'collapse',
                    flexGrow: '1',
                }}>
                <Box p={2}>{content}</Box>
            </Grid>
        );
    }, []);

    const cityStateZip = useMemo(() => {
        if (currentQuote) {
            return `${currentQuote?.city}, ${currentQuote?.state}, ${currentQuote?.zipCode}`;
        } else {
            return '';
        }
    }, [currentQuote]);

    const generalDetails = useCallback((orderReview: OrderReviewDto, orderLineItem: OrderReviewLineItemDto) => {
        const isMaterial = isMaterialLineItem(orderLineItem);
        const isHourly = orderLineItem?.lineItem?.type === LineItemTypes.Hourly;
        const isMisc = orderLineItem?.lineItem?.type === LineItemTypes.Misc;
        const hideSiteAndZone = isHourly || isMisc;
        if (!orderReview) {
            return <Box p={2} />;
        }

        return (
            <Box p={2}>
                <Grid item container direction='column' spacing={1}>
                    <Grid container direction='row' spacing={1}>
                        <Grid container spacing={1} alignItems='center'>
                            <Grid item xs={12} sm={8} md={8}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} ml={2}>
                                        <FormInput
                                            label='Project Name'
                                            name='projectName'
                                            value={currentQuote?.projectName ?? ''}
                                            onChange={() => { }}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={12} ml={2}>
                                        <FormInput
                                            label='Job Name'
                                            name='jobName'
                                            value={currentQuote?.jobName ?? ''}
                                            onChange={() => { }}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={4} md={4} container justifyContent="center">
                                <Grid item xs={12} ml={2}>
                                    {gridItem(
                                        <>
                                            <Typography>Project Address:</Typography>
                                            <Typography>{currentQuote?.addressLine1 ?? ''}</Typography>
                                            {currentQuote?.addressLine2 && <Typography>{currentQuote?.addressLine2}</Typography>}
                                            <Typography>{cityStateZip}</Typography>
                                            <Typography>{currentQuote?.country}</Typography>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item container direction='row' xs={12} alignItems='center' justifyContent='space-evenly'>
                        <OrderRelatedLinks order={orderReview} />
                    </Grid>
                    <Grid item container xs={12} spacing={1} alignItems='center'>
                        <Grid item xs={4}>
                            <DatePicker
                                label='Order Date'
                                value={orderReview.orderDate}
                                onChange={() => { }}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl fullWidth disabled>
                                <FormLabel>Contact</FormLabel>
                                <IEntityAutocomplete
                                    options={[]}
                                    disabled
                                    value={orderReview?.contact}
                                    getOptionLabel={(option: ContactDto) =>
                                        option.firstName +
                                        (option.lastName ? ' ' + option.lastName : '') +
                                        (option.title ? ' - ' + option.title : '') +
                                        (option.cellPhone ? ' - ' + option.cellPhone : '')
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={4} container direction='row' alignItems='center'>
                            <FormControl disabled sx={{ mt: 3 }}>
                                <FormControlLabel control={<Checkbox disabled checked={orderReview?.isTaxable} onChange={() => { }} />} label='Is Taxable' />
                            </FormControl>
                            <Grid item mt={3}>
                                Line Item: <Link
                                    variant='body2'
                                    href={`/quote/${orderReview.quoteId}/lineItem/${orderLineItem.lineItem?.type}/${orderLineItem.lineItem?.id}/`}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {orderLineItem.lineItem?.type}
                                </Link>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item container direction='row' xs={12} spacing={1} wrap='nowrap'>
                        <Grid item xs={2}>
                            <FormNumberInput
                                value={orderLineItem?.orderLineItemNumber}
                                name='orderLineItemNumber'
                                label='Line Item #'
                                fullWidth
                                disabled
                                onChange={() => { }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormInput
                                value={orderLineItem?.description}
                                onChange={() => { }}
                                label='Description'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormInput
                                value={orderLineItem.poNumber}
                                label='PO Number'
                                fullWidth
                                onChange={() => { }}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormInput
                                value={orderLineItem.customerJobNumber}
                                label='Job Number'
                                fullWidth
                                onChange={() => { }}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={hideSiteAndZone ? 4 : 2}>
                            <EquipmentTypeSelect
                                selectedEquipmentTypeId={orderLineItem?.equipmentType?.id ?? ''}
                                handleSelectedEquipmentTypeChange={() => { }}
                                disabled
                            />
                        </Grid>
                        {!hideSiteAndZone && (
                            <>
                                <Grid item xs={2}>
                                    <FormInput
                                        value={orderLineItem?.lineItem?.site?.name}
                                        onChange={() => { }}
                                        label='Site'
                                        fullWidth
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormInput
                                        value={orderLineItem?.lineItem?.zone}
                                        onChange={() => { }}
                                        label='Zone'
                                        fullWidth
                                        disabled
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Grid item container direction='row' xs={12} spacing={1} wrap='nowrap'>
                        {isMaterial && <Grid item xs={3}>
                            <PricingMethodTypeSelect
                                selectedPricingMethodType={orderLineItem?.billingRates!.pricingMethodType!}
                                onChange={() => { }}
                                disabled
                            />
                        </Grid>}
                        <Grid item xs={isMaterial ? 2 : 4}>
                            <FormNumberInput
                                value={orderLineItem?.billingRates?.salesPrice ?? orderLineItem?.billingRates?.quoteRate ?? orderLineItem?.billingRates?.lineItemRate}
                                onChange={() => { }}
                                label={`Rate ${orderLineItem?.billingRates?.flatRateApplied && isMaterial ? '(+ Flat)' : ''}`}
                                name='lineItemRate'
                                fullWidth
                                disabled
                                isCurrency
                            />
                        </Grid>
                        <Grid item xs={isMaterial ? 2 : 4}>
                            <FormNumberInput
                                label='Quantity'
                                name='quantity'
                                value={orderLineItem?.billingRates?.quantity}
                                onChange={() => { }}
                                fullWidth
                                inputProps={{ min: 0 }}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={isMaterial ? 3 : 4}>
                            {!!orderLineItem.lineItemId ? <FormInput
                                value={orderLineItem?.billingRates?.unitOfMeasure}
                                onChange={() => { }}
                                label='Units'
                                fullWidth
                                disabled
                            /> :
                                <UnitOfMeasureSelect
                                    selectedUnit={orderLineItem?.billingRates?.unitOfMeasure ?? ''}
                                    handleSelectedUnitChange={() => { }}
                                    disabled
                                />}
                        </Grid>
                        {isMaterial && <Grid item xs={2}>
                            <FormInput
                                value={
                                    orderLineItem.lineItem?.isBilledTo === BilledToType.TriAx ? 'TRI AX INC' :
                                        orderLineItem.lineItem?.isBilledTo === BilledToType.Customer ? orderLineItem.customerName :
                                            orderLineItem.lineItem?.isBilledTo === BilledToType.Custom ? orderLineItem.lineItem?.billedTo ?? '' : ''
                                }
                                onChange={() => { }}
                                label='Material Bill To'
                                fullWidth
                                disabled
                            />
                        </Grid>}
                    </Grid>
                    <Grid item container spacing={1} xs={12}>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.yardTime}
                                onChange={() => { }}
                                label={`Yard Time`}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.onsiteTime}
                                onChange={() => { }}
                                label={`Onsite Time`}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <DatePicker
                                value={orderReview.quote?.expirationDate}
                                onChange={() => { }}
                                label={`Quote Expiration Date`}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <OrderBySelect
                                selectedOrderBy={orderLineItem.orderBy!}
                                onChange={() => { }}
                                disabled
                            />
                        </Grid>
                        {orderLineItem.orderBy !== OrderByType.Equipment && (
                            <Grid item xs={3}>
                                <FormNumberInput
                                    value={orderLineItem.loadOrQuantityReq}
                                    name='loadOrQuantityReq'
                                    onChange={() => { }}
                                    label='Quantity'
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    disabled
                                />
                            </Grid>
                        )}
                        {!orderLineItem.orderBy && (
                            <Grid item xs={3}>
                                <FormNumberInput
                                    value={orderLineItem.equipmentRequired}
                                    name='equipmentRequired'
                                    onChange={() => { }}
                                    label='Equipment Req.'
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    disabled
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Box>
        );
    }, [isMaterialLineItem, gridItem,
        cityStateZip, currentQuote?.addressLine1, currentQuote?.addressLine2, currentQuote?.country,
        currentQuote?.jobName, currentQuote?.projectName]);

    return (
        <>{generalDetails(orderReview, orderLineItem)}</>
    );
};